import { useQuery } from "@tanstack/react-query";
import { useEffect, useEffectEvent, useRef } from "react";

import type { EligibilityRequest } from "../../../types";

import { getEligibility } from "../../../api/eligibility";
import { isInvalidMdnError } from "../../../api/eligibility.errors";
import { useSalesContext } from "../../../sales-context/SalesContext";
import { isInternationalCaller } from "../../../shared-utils/formatters/validatePhoneNumber";
import { useCallDetailsStore, useProductOfferStore, useVerifiedCustomerStore } from "../../../stores";
import { parsePhoneNumberAttributes } from "../../product-sale-flow/utils/parsePhoneNumberAttributes";

const QUERY_KEY = "sales-eligibility";
const EXPERT_UNIVERSITY_SUFFIX = "test-training";

export { QUERY_KEY };

export function useEligibility(isEnabled = true) {
    const {
        sessionId,
        partner,
        logger,
        analytics,
        taskType,
        taskData,
        apiClients,
        expertLocation,
        onEligibilityResult,
        features,
    } = useSalesContext();
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const hasProducts = useProductOfferStore((state) => (state.getProducts(sessionId)?.length ?? 0) > 0);

    const callDetails = useCallDetailsStore((state) => state.callDetails);
    const isCustomerInProtectedState = callDetails.PROTECTED_SALES === "1";
    const isExpertUniversitySimulatedVoiceTask = sessionId.includes(EXPERT_UNIVERSITY_SUFFIX);
    // Nothing writes customer.metadata.isInternationalCaller today — its only writer was the
    // McAfee-SaaS call-intro flow, now deleted. The ANI fallback is what supplies the attribute;
    // the metadata read stays so a future expert-entered value would still take precedence.
    const isInternational = customer?.metadata?.isInternationalCaller ?? isInternationalCaller(taskData.mdn);

    const {
        data: eligibilityResult,
        error,
        isError,
        isLoading,
    } = useQuery({
        queryKey: [
            QUERY_KEY,
            sessionId,
            customer?.mdn,
            customer?.accountNumber,
            // NOTE: email is intentionally NOT in the queryKey. Adding it (PR #6121,
            // ported from legacy PR #6037) caused useEligibility to re-fetch when
            // setCustomer wrote email, racing useCollectCustomerInfo's call inside
            // pDebounce(eligibility, 500ms). The trailing-edge fire used
            // useEligibility's no-firstName/lastName body, which sales-domain
            // Object.assign'd over the session's name fields, breaking smart-link
            // generation. See RDESQ-2032 (legacy fix shipped in PR #6178).
            customer?.currentSubscriptions,
            // callDetails.CLIENT IS in the queryKey (unlike the fields above): it's an
            // eligibilityAttributes input the backend uses to shape the response (e.g. Verizon
            // IBC/OFR primary-card treatment), and it can legitimately arrive after the first
            // eligibility check fires (channel-share data lands after customer verification).
            // Without it here, staleTime: Infinity means a late CLIENT value never triggers a
            // refetch, so the backend never gets a chance to reshape the offer for it.
            callDetails.CLIENT,
            // In the key for the same reason: chat MDNs decrypt asynchronously, so the ANI-derived
            // value can flip after the first check and must refetch rather than stay cached.
            isInternational,
        ],
        queryFn: async () => {
            logger.info("Checking eligibility");
            return getEligibility(
                apiClients.sales,
                logger,
                analytics,
                {
                    asurionCallId: taskType.isVoice ? taskData.asurionCallId : undefined,
                    partner,
                    sessionId,
                    customerInteractionId: taskData.customerInteractionId,
                    reservationSid: taskData.reservationSid ?? sessionId,
                    ...(isExpertUniversitySimulatedVoiceTask
                        ? { customerMdn: taskData.mdn }
                        : { ...parsePhoneNumberAttributes(customer?.mdn) }),
                    address: customer?.address,
                    customer: {
                        firstName: customer?.firstName,
                        lastName: customer?.lastName,
                        email: customer?.email,
                        accountNumber: customer?.accountNumber,
                    },
                    eligibilityAttributes: {
                        currentPlan: customer?.productName,
                        isInternationalCaller: isInternational,
                        chatSourceApp: taskType.isChat ? taskData.chatSourceApp : undefined,
                        currentSubscriptions: customer?.currentSubscriptions,
                        expertLocation,
                        isCustomerInProtectedState,
                        client: callDetails.CLIENT,
                    },
                    salesChannel: taskData.salesChannel as EligibilityRequest["salesChannel"],
                },
                features.isUsingMovedDiscoveryAndEngagementQuestions,
            );
        },
        // Do NOT gate on `!hasProducts`: that made the query sticky for the whole session, so a
        // change to an eligibility input (mdn/account/subscriptions, all in the queryKey) never
        // refreshed the offers and the expert could enroll a stale/ineligible offer. react-query's
        // queryKey + `staleTime: Infinity` already dedupes repeat checks for unchanged inputs, so
        // dropping the gate refreshes on input change without adding calls in the common case.
        enabled: isEnabled && !!customer?.mdn,
        staleTime: Infinity,
        gcTime: Infinity,
        // An invalid MDN is deterministic — retrying won't change the result, and the UI
        // handles it with an inline correction view. Other failures still get one retry.
        retry: (failureCount, queryError) => (isInvalidMdnError(queryError) ? false : failureCount < 1),
    });

    useEffect(() => {
        if (eligibilityResult) {
            const store = useProductOfferStore.getState();
            store.updateProducts(sessionId, eligibilityResult.productConfigs, eligibilityResult.displayCues);
            if (eligibilityResult.meta) {
                store.updateEligibilityMetaBySessionId(sessionId, eligibilityResult.meta);
            }
            onEligibilityResult?.(eligibilityResult.productConfigs);
        }
    }, [eligibilityResult, sessionId, onEligibilityResult]);

    // Surface the case where the eligibility query would have fired except
    // customer.mdn is empty. Today this is the FIOS BAN-flow failure mode:
    // BAN lookup didn't return an mdn and the agent left the form blank.
    const logGateBlock = useEffectEvent((reason: string) => {
        logger.warn({ partner, sessionId, reason }, "Eligibility query disabled");
    });
    // Warn at most once per session — the gate condition can toggle as the agent
    // works the panel, and we only want to flag the session, not every re-render.
    const gateBlockLoggedForSession = useRef<string | null>(null);
    useEffect(() => {
        if (isEnabled && !hasProducts && !customer?.mdn && gateBlockLoggedForSession.current !== sessionId) {
            gateBlockLoggedForSession.current = sessionId;
            logGateBlock("missing customer.mdn");
        }
    }, [isEnabled, hasProducts, customer?.mdn, sessionId]);

    return { eligibilityResult, error, isError, isLoading };
}

export function clearEligibilityQueryCache(
    queryClient: { removeQueries: (opts: { predicate: (query: { queryKey: readonly unknown[] }) => boolean }) => void },
    sessionId: string,
) {
    queryClient.removeQueries({
        predicate: (query) => query.queryKey.includes(QUERY_KEY) && query.queryKey.includes(sessionId),
    });
}
